import {
  type EnvironmentProviders,
  type Provider,
  type WritableSignal,
  computed,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { TUI_DARK_MODE } from '@taiga-ui/core/tokens';

import { ThemeService } from './theme.service';
import { ThemeStore } from './theme.store';

type TuiDarkMode = WritableSignal<boolean> & { reset(): void };

/**
 * Bridges Taiga UI's own dark-mode token onto our tri-state store.
 *
 * Taiga's default factory persists its own boolean in `localStorage`, which
 * would give the application two competing sources of truth. Delegating keeps
 * every Taiga component that reads `TUI_DARK_MODE` in sync with the store,
 * and `reset()` maps onto "follow the system" — exactly its documented meaning.
 */
function provideTaigaDarkModeBridge(): Provider {
  return {
    provide: TUI_DARK_MODE,
    useFactory: (): TuiDarkMode => {
      const store = inject(ThemeStore);
      const isDark = computed(() => store.isDark());

      return Object.assign(isDark, {
        set: (value: boolean) => store.setMode(value ? 'dark' : 'light'),
        update: (updater: (value: boolean) => boolean) =>
          store.setMode(updater(isDark()) ? 'dark' : 'light'),
        asReadonly: () => isDark,
        reset: () => store.setMode('system'),
      }) as unknown as TuiDarkMode;
    },
  };
}

/**
 * Theme infrastructure. The initializer instantiates `ThemeService` before the
 * first render so the correct palette is applied without a flash.
 */
export function provideTheme(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideTaigaDarkModeBridge(),
    provideAppInitializer(() => {
      inject(ThemeService);
    }),
  ]);
}
